import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  Fragment,
} from 'react';
import { MDBCol, MDBRow } from 'mdbreact';
import { get, post } from 'axios';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { actionSvgs, contentSvgs } from 'assets/icons';

import ActionsBox from 'components/ActionsBox';
import ContentLoader from 'components/Loaders/ContentLoader';
import FixedGrid from 'components/FixedGrid';
import PageStructure from 'components/PageStructure';
import ProgressBar from 'components/ProgressBar';

import { renderToast } from 'components/Informations/Toast/utils';

import FilledButton from 'components/Buttons/FilledButton';
import UnfilledButton from 'components/Buttons/UnfilledButton';

import InputField from 'components/FormGroup/InputField';
import SelectField from 'components/FormGroup/SelectField';

import useFetch from 'hooks/useFetch';
import endpoints from 'services/endpoints';
import colors from 'themes/Main';
import AreaAccordion from '../../InternalComponents/AreaAccordion';
import QuestionItem from '../../InternalComponents/Questions/QuestionItem';
import QuestionBlock from '../../InternalComponents/Questions/QuestionBlock';
import QuestionContainer from '../../InternalComponents/Questions/QuestionContainer';
import {
  ModalAreas,
  RemoveArea,
  ModalBlocks,
  RemoveBlock,
  ModalQuestions,
  RemoveQuestion,
  ModalLanguages,
  AddSoftSkill,
  ModalSoftSkills,
} from '../../InternalComponents/Modals/Configurations';

import * as S from './styles';
import * as U from './utils';

const { darkBlue } = colors;
const { getTeam, getConfigsForm, saveConfigsForm } = endpoints;

export default function Configurations({ user, history }) {
  const { id_funcionario: userID, administrador } = user;
  const isAdmin = administrador === 1;

  const [teamId, setTeamId] = useState('');

  const [stateCollection, setStateCollection] = useState({});
  const [loading, setLoading] = useState(true);

  const [activedArea, setActivedArea] = useState(NaN);

  const [modalSS, setModalSS] = useState(false);
  const [modalSSLang, setModalSSLang] = useState(false);
  const [modalSSBlock, setModalSSBlock] = useState(false);
  const [modalArea, setModalArea] = useState(false);
  const [modalBlock, setModalBlock] = useState(false);
  const [modalQuestion, setModalQuestion] = useState(false);
  const [modalDelete, setModalDelete] = useState(false);

  const [modalType, setModalType] = useState('');
  const [modalData, setModalData] = useState({});
  const [toastVisibility, setToastVisibility] = useState(false);
  const [toastInfos, setToastInfos] = useState({
    type: '',
    message: '',
    autoClose: 6000,
  });

  const [functionsSelect, setFunctionsSelect] = useState([]);
  const [selectedFunction, setSelectedFunction] = useState(0);
  const [isChanged, setIsChanged] = useState(false);

  const [form, setForm] = useState([]);

  const [formObserver, setFormObserver] = useState(false);
  const [allFunctions] = useFetch(`${getTeam}/${userID}/${isAdmin ? 1 : 0}`);

  useEffect(() => {
    setLoading(true);

    (async () => {
      const functionsResponse = await allFunctions;
      const { id_equipe: id } = functionsResponse[0] ?? '';
      const selectFunctions = await functionsResponse
        ?.map(({ funcao }) =>
          funcao.map(({ id_funcao: id, funcao: nomeFuncao }) => ({
            label: nomeFuncao,
            value: id,
          })),
        )
        .flat();

      setSelectedFunction(
        currentValue => currentValue || selectFunctions[0]?.value,
      );
      setFunctionsSelect(selectFunctions);

      if (functionsResponse?.length) {
        const { data: formResponse } =
          (await allFunctions) &&
          (await get(
            `${getConfigsForm}/${userID}/${id}/${
              selectedFunction || selectFunctions[0]?.value
            }`,
          ));

        if (formResponse?.length) {
          const statesCollection = await U.generateStates(formResponse);

          const mergedCollection = isChanged
            ? statesCollection
            : _.merge(statesCollection, stateCollection);

          setStateCollection(mergedCollection);
          setForm(formResponse);

          setLoading(false);
          setIsChanged(false);
        }
      }

      setTeamId(id);
    })();
  }, [allFunctions, selectedFunction, formObserver, setFormObserver, userID]);

  const progressBarValue = useMemo(() => {
    if (Object.keys(stateCollection).length) {
      const importances = Object.values(stateCollection?.blocos);
      return importances.reduce((acc, curr) => acc + curr);
    }

    return 0;
  }, [stateCollection]);

  const handleActiveAccordion = activedIndex => e => {
    e.persist();
    const { checked: actived } = e.target;

    if (actived) {
      setActivedArea(activedIndex);
    }
  };

  const handleSetImportanceBlock = useCallback(
    (iArea, iBloco) => e => {
      e.persist();
      const { value } = e.target;

      const key = `${iArea}-${iBloco}`;
      setStateCollection(prev => {
        const { blocos } = prev;
        return {
          ...prev,
          blocos: {
            ...blocos,
            [key]: Number(value),
          },
        };
      });
    },
    [],
  );

  const handleSetQuestionFiled = useCallback(
    (iA, iB, iQ, field) => e => {
      const keyChanged = `${iA}-${iB}-${iQ}`;
      const { value } = e.target;
      const conditionedValue = U.attributionCondition(value);

      setStateCollection(prev => ({
        ...prev,
        questoes: {
          ...prev?.questoes,
          [keyChanged]: {
            ...prev?.questoes?.[keyChanged],
            [field]: conditionedValue,
          },
        },
      }));
    },
    [],
  );

  const RenderModalDelete = deleteType => {
    switch (deleteType) {
      case 'question':
        return (
          <RemoveQuestion
            data={modalData}
            open={modalDelete}
            setOpen={setModalDelete}
          />
        );
      case 'block':
        return (
          <RemoveBlock
            data={modalData}
            open={modalDelete}
            setOpen={setModalDelete}
          />
        );
      case 'area':
        return (
          <RemoveArea
            data={modalData}
            open={modalDelete}
            setOpen={setModalDelete}
          />
        );
      default:
        return '';
    }
  };

  const RenderModalBlock = useMemo(
    () =>
      modalBlock && (
        <ModalBlocks
          open={modalBlock}
          setOpen={setModalBlock}
          data={modalData}
          type={modalType}
        />
      ),
    [modalBlock, modalData, modalType],
  );

  const RenderModalArea = useMemo(
    () =>
      modalArea && (
        <ModalAreas
          open={modalArea}
          setOpen={setModalArea}
          data={modalData}
          type={modalType}
        />
      ),
    [modalArea, modalData, modalType],
  );

  const RenderModalSS = useMemo(
    () =>
      modalSS && (
        <AddSoftSkill
          open={modalSS}
          setOpen={setModalSS}
          data={modalData}
          type={modalType}
        />
      ),
    [modalSS, modalData, modalType],
  );

  const RenderModalSSLang = useMemo(
    () =>
      modalSSLang && (
        <ModalLanguages
          open={modalSSLang}
          setOpen={setModalSSLang}
          data={modalData}
          type={modalType}
        />
      ),
    [modalSSLang, modalData, modalType],
  );

  const RenderModalSSBlock = useMemo(
    () => (
      <ModalSoftSkills
        open={modalSSBlock}
        setOpen={setModalSSBlock}
        data={modalData}
        type="add"
      />
    ),
    [modalSSBlock, modalData],
  );

  const RenderModalQuestion = useMemo(
    () =>
      modalQuestion && (
        <ModalQuestions
          open={modalQuestion}
          setOpen={setModalQuestion}
          data={modalData}
          type={modalType}
        />
      ),
    [modalQuestion, modalType, modalData],
  );

  const renderQuestions = (area, iArea, bloco, iBloco) => (
    question,
    iQuest,
  ) => {
    const key = `${iArea}-${iBloco}-${question.id_questao}`;
    const currentWeight = stateCollection?.questoes[key]?.peso;
    const currentDificulty = stateCollection?.questoes[key]?.dificuldade;

    return (
      <QuestionItem key={`${iArea}-${iBloco}-${iQuest}`}>
        <div>{question?.nome}</div>
        <S.QuestionControllersWrap className={isAdmin ? 'isAdmin' : ''}>
          {!isAdmin && (
            <>
              <div>
                <InputField
                  type="number"
                  name={`question-weight-${iArea}-${iBloco}-${question.id_questao}`}
                  value={currentWeight}
                  labelText="Peso"
                  placeholderText="Peso da questão"
                  handleChange={handleSetQuestionFiled(
                    iArea,
                    iBloco,
                    question.id_questao,
                    'peso',
                  )}
                  width={60}
                />
              </div>
              <div>
                <InputField
                  type="number"
                  name={`question-weight-${iArea}-${iBloco}-${question.id_questao}`}
                  value={currentDificulty}
                  labelText="Dificuldade"
                  placeholderText="Dificuldade da questão"
                  handleChange={handleSetQuestionFiled(
                    iArea,
                    iBloco,
                    question.id_questao,
                    'dificuldade',
                  )}
                />
              </div>
            </>
          )}
          {((!bloco?.nome?.match(/Idiomas/g) && isAdmin) ||
            area.id_responsavel === userID) && (
            <div>
              <UnfilledButton
                handleClick={() => {
                  const data = {
                    options: area?.blocos?.map(i => ({
                      label: i.nome,
                      value: i.id_bloco,
                    })),
                    selected: { label: bloco?.nome, value: bloco?.id_bloco },
                    nome: question.nome,
                    descricao: question?.descricao,
                    id_bloco: bloco.id_bloco,
                    id_area: area.id_area,
                    id_questao: question.id_questao,
                    id_tipo_questao: question.id_tipo_questao,
                    id_funcionario: userID,
                    observer: () => setFormObserver(current => !current),
                  };
                  setModalType('edit');

                  if (
                    area?.descricao?.match(
                      /Comportamental|Direcionamento da Carreira/g,
                    )
                  ) {
                    data.level = question?.id_tipo_questao;
                    data.alternatives = question?.opcoes_resposta;
                    setModalSS(true);
                  } else {
                    setModalQuestion(true);
                  }
                  setModalData(data);
                }}
              >
                {actionSvgs('edit', darkBlue)}
              </UnfilledButton>
              <UnfilledButton
                handleClick={() => {
                  setModalDelete(true);
                  setModalType('question');
                  setModalData({
                    id_questao: question.id_questao,
                    id_funcionario: userID,
                    observer: () => setFormObserver(current => !current),
                  });
                }}
              >
                {actionSvgs('remove')}
              </UnfilledButton>
            </div>
          )}
        </S.QuestionControllersWrap>
      </QuestionItem>
    );
  };

  const renderBlocks = (area, iArea) => (bloco, iBloco) => {
    const val = stateCollection?.blocos?.[`${iArea}-${bloco.id_bloco}`];
    const areaImportance = U.sumAreaimportace(stateCollection?.blocos, iArea);
    const isSoftskills = area?.nome?.match(/Comportamental/g);

    const redirections = [
      {
        label: 'Editar bloco',
        onClick: () => {
          const data = {
            id_area: area.id_area,
            nome: bloco.nome,
            descricao: bloco.descricao,
            id_bloco: bloco.id_bloco,
            id_funcionario: userID,
            observer: () => setFormObserver(current => !current),
          };

          if (bloco?.nome?.match(/Idiomas/g)) {
            setModalSSLang(true);
            data.questoes = bloco?.questoes;
          } else {
            setModalBlock(true);
          }

          setModalType('edit');
          setModalData(data);
        },
      },
      {
        label: 'Deletar bloco',
        onClick: () => {
          setModalDelete(true);
          setModalType('block');
          setModalData({
            id_funcionario: userID,
            id_bloco: bloco.id_bloco,
            observer: () => setFormObserver(current => !current),
          });
        },
      },
      {
        label: 'Criar novo bloco',
        onClick: () => {
          isSoftskills ? setModalSS(true) : setModalBlock(true);
          setModalType('add');
          setModalData({
            id_area: area.id_area,
            id_funcionario: userID,
            observer: () => setFormObserver(current => !current),
          });
        },
      },
    ];

    return (
      <Fragment key={`${iArea}-${iBloco}`}>
        <QuestionBlock blockName={bloco.nome}>
          {!isAdmin && (
            <>
              <S.BlockHeaderDiv isFull={!isAdmin}>
                <InputField
                  percent
                  type="number"
                  labelText="Importância"
                  width={50}
                  value={val}
                  handleChange={handleSetImportanceBlock(iArea, bloco.id_bloco)}
                  onBlur={handleSetImportanceBlock(iArea, bloco.id_bloco)}
                  hasLabelMargin={false}
                />
              </S.BlockHeaderDiv>
              <S.BlockHeaderDiv isFull={!isAdmin}>
                <S.Importance level={areaImportance}>
                  {`${val || 0} / 100%`}
                </S.Importance>
              </S.BlockHeaderDiv>
            </>
          )}

          {(area.id_responsavel === userID || isAdmin) && (
            <S.BlockHeaderDiv className="actions-box" isFull={!isAdmin}>
              {contentSvgs('moreInfos', darkBlue)}
              <ActionsBox
                hasLinks={false}
                headText="Ações"
                redirections={redirections}
              />
            </S.BlockHeaderDiv>
          )}
        </QuestionBlock>
        <QuestionContainer>
          {bloco?.questoes?.map(renderQuestions(area, iArea, bloco, iBloco))}
        </QuestionContainer>
      </Fragment>
    );
  };

  const renderAccordions = (area, iArea) => (
    <AreaAccordion
      accordionName="areas"
      accordionId={`${area.descricao}-iarea`}
      headTitle={area.descricao}
      key={area.id_area}
      handleActive={handleActiveAccordion(iArea)}
      Actions={() => isAdmin && AreaOptions(area)}
    >
      {activedArea === iArea &&
        userID &&
        area?.blocos.map(renderBlocks(area, iArea))}

      {(isAdmin || area.id_responsavel === userID) && (
        <S.AreaButtonsWrap>
          <FilledButton
            text="+ Adicionar nova questão"
            buttonType="button"
            buttonClass="cancel"
            handleClick={() => {
              setModalType('add');
              if (area?.descricao?.match(/Comportamental/g)) {
                setModalSS(true);
              } else {
                setModalQuestion(true);
              }
              setModalData({
                options: area?.blocos?.map(i => ({
                  label: i.nome,
                  value: i.id_bloco,
                })),
                id_funcionario: userID,
                id_area: area.id_area,
                observer: () => setFormObserver(current => !current),
              });
            }}
          />
          <FilledButton
            text="+ Adicionar novo bloco"
            buttonType="button"
            handleClick={() => {
              setModalType('add');

              if (area?.descricao?.match(/Comportamental/g)) {
                setModalSSBlock(true);
              } else {
                setModalBlock(true);
              }

              setModalData({
                id_area: area.id_area,
                id_funcionario: userID,
                observer: () => setFormObserver(current => !current),
              });
            }}
          />
        </S.AreaButtonsWrap>
      )}
    </AreaAccordion>
  );

  const renderAreas = (area, iArea) => renderAccordions(area, iArea);

  const areaRedirections = area => [
    {
      label: 'Editar dados da área',
      onClick: () => {
        setModalArea(true);
        setModalType('edit');
        setModalData({
          ...area,
          id_funcionario: userID,
          id_equipe: teamId,
          observer: () => setFormObserver(current => !current),
        });
      },
    },
    {
      label: 'Deletar área',
      onClick: () => {
        setModalDelete(true);
        setModalType('area');
        setModalData({
          ...area,
          id_funcionario: userID,
          observer: () => setFormObserver(current => !current),
        });
      },
    },
    {
      label: 'Criar nova área',
      onClick: () => {
        setModalArea(true);
        setModalData({
          ...area,
          id_funcionario: userID,
          id_equipe: teamId,
          observer: () => setFormObserver(current => !current),
        });
        setModalType('add');
      },
    },
  ];

  const AreaOptions = useCallback(
    area => (
      <S.AreaActions>
        {contentSvgs('moreInfos', '#ffffff')}
        <ActionsBox
          headText="Ações"
          hasLinks={false}
          redirections={areaRedirections(area)}
        />
      </S.AreaActions>
    ),
    [teamId, areaRedirections],
  );

  const handleSubmit = () => {
    const { blocos, questoes } = stateCollection;
    const mappedBlocks =
      blocos &&
      Object.entries(blocos).map(item => ({
        id_bloco: Number(item[0].split('-')[1]),
        importancia: item[1],
      }));

    const mappedQuestions =
      questoes &&
      Object.entries(questoes).map(item => ({
        id_questao: Number(item[0].split('-')[2]),
        ...item[1],
      }));

    if (mappedBlocks && mappedQuestions) {
      const objectToSend = {
        id_usr_in: userID,
        id_equipe: teamId,
        blocos: mappedBlocks,
        questoes: mappedQuestions,
        id_funcao: selectedFunction,
      };

      return post(saveConfigsForm, objectToSend)
        .then(() => {
          setToastVisibility(true);
          setToastInfos({
            type: 'success',
            message: 'Configurações salvas com sucesso',
            autoClose: 6000,
          });
        })
        .then(() => {
          setTimeout(() => {
            history.push('/auth/pdi');
          }, 4000);
        })
        .catch(() => {
          setToastVisibility(true);
          setToastInfos({
            type: 'error',
            message: 'Erro ao salvar alterações',
            autoClose: 6000,
          });
        });
    }
  };

  return (
    <>
      {toastVisibility &&
        renderToast(
          toastInfos.type,
          toastInfos.message,
          toastInfos.autoClose,
          setToastVisibility,
        )}

      <PageStructure
        title="Plano de Desenvolvimento Individual - Configurações"
        subtitle="Atribua um valor percentual de importância para cada bloco da sua área.
        Selecione um peso e uma dificuldade para cada uma das questões da sua ou outras áreas.
        Edite ou delete questões e blocos caso necessário."
      >
        <MDBRow>
          <MDBCol xs="12" md="9">
            {!isAdmin && (
              <S.FunctionSelectContainer>
                <SelectField
                  hasSecondaryStyle
                  hasLabelMargin
                  labelText="Editando o formulário para a função *"
                  placeholderText="Selecione uma função..."
                  handleChange={({ value }) => {
                    setSelectedFunction(value);
                    setIsChanged(true);
                  }}
                  name="selectFunction"
                  value={functionsSelect?.find(
                    ({ value }) =>
                      value === selectedFunction ||
                      value === selectedFunction?.current,
                  )}
                  options={functionsSelect}
                />
                <S.SmallText>
                  * Salve as alterações antes de selecionar outra função
                </S.SmallText>
              </S.FunctionSelectContainer>
            )}

            {form?.map(renderAreas)}

            <S.SubmitContainer>
              <FilledButton
                text="Salvar alterações"
                handleClick={handleSubmit}
                disabled={progressBarValue > 120}
                hasTooltip
                tooltipText="A importância atribuída ultrapassa o máximo de 120%"
              />
            </S.SubmitContainer>
          </MDBCol>
          <MDBCol xs="12" md="3">
            <FixedGrid>
              {!isAdmin && (
                <ProgressBar
                  headText="Importância atribuída"
                  hasCounter
                  totalValue={100}
                  currentValue={progressBarValue}
                  hasPercentage
                />
              )}
              <S.CommentBox>
                {isAdmin ? (
                  <>
                    <p>
                      Você pode editar, deletar e criar novas áreas, blocos ou
                      questões em todas as áreas.
                    </p>
                    <p>Suas alterações no formulário refletirão para todos.</p>
                  </>
                ) : (
                  <>
                    <p>
                      Atribua % de importância de cada bloco da sua área (ou
                      outras) até chegar a 100%.
                    </p>
                    <p>
                      Lembre-se de deixar um valor para a área de
                      Comportamental, onde será possível atribuir um extra de
                      20%. Ou seja, o total distribuído poderá chegar a 120%.
                    </p>
                    <p>Peso e dificuldade variam entre 0 e 1.</p>
                    <p>
                      Você pode editar, deletar e criar novos blocos ou questões
                      para a sua área.
                    </p>
                  </>
                )}
              </S.CommentBox>
            </FixedGrid>
          </MDBCol>
        </MDBRow>
      </PageStructure>

      {loading && <ContentLoader isFull />}

      {modalBlock ? RenderModalBlock : <></>}
      {modalQuestion ? RenderModalQuestion : <></>}
      {modalArea ? RenderModalArea : <></>}
      {modalDelete ? RenderModalDelete(modalType) : <></>}
      {modalSS ? RenderModalSS : <></>}
      {modalSSLang ? RenderModalSSLang : <></>}
      {modalSSBlock ? RenderModalSSBlock : <></>}
    </>
  );
}

Configurations.propTypes = {
  user: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ).isRequired,
  history: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  ).isRequired,
};
